import { useState, useCallback } from "react";
import { getAccessTokenSilently } from "utils/functions";

export const useFetchData = (apiFn, authRequired = true) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const stableApiFn = useCallback(apiFn, [apiFn]);

  const fetchData = useCallback(
    async (...args) => {
      setLoading(true);
      try {
        let token;
        if (authRequired) {
          token = await getAccessTokenSilently();
        }
        const response = authRequired
          ? await stableApiFn(token, ...args)
          : await stableApiFn(...args);
        setData(response);
        return response;
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [stableApiFn, authRequired]
  );

  const fetchWithDelay = useCallback(
    (...args) => {
      const timer = setTimeout(() => {
        fetchData(...args);
      }, 100);
      return () => clearTimeout(timer);
    },
    [fetchData]
  );

  return { data, loading, fetchData, fetchWithDelay };
};

export const useApiAction = (apiFn, authRequired = true) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const execute = useCallback(
    async (...args) => {
      setLoading(true);
      try {
        let token;
        if (authRequired) {
          token = await getAccessTokenSilently();
        }
        const response = authRequired
          ? await apiFn(token, ...args)
          : await apiFn(...args);
        setData(response);
        return response;
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [apiFn, authRequired]
  );

  return { execute, loading, data, setData };
};
